import { useEffect, useState } from 'react'
import { Button, Card } from '@heroui/react'
import { Notice } from '../admin/settingsKit'
import { api, type BranchSeat, type SignageNow, type SignagePlace } from '../lib/api'
import { explain } from '../lib/staffErrors'
import { branchZone, changeMoment, momentBounds, momentHeading, momentParam, nowBranch, PLACE_KEYS, PLACES, refreshIn } from '../lib/signageEdit'
import { useStaffText } from '../ui/StaffText'
import { MomentField, PickOne } from './signageKit'
import { Thumb } from './signageLibrary'

/** What a place at a branch plays now, or at a chosen moment in branch time, and until when, as the server would decide it.
 *  stamp changes with every campaign save, pause or delete, and the panel asks again. */
export function NowPanel({ branches, stamp }: { branches: readonly BranchSeat[]; stamp: string }) {
  const { say, lang } = useStaffText()
  const [branchId, setBranchId] = useState('')
  const [place, setPlace] = useState<SignagePlace>('board')
  const [now, setNow] = useState<SignageNow | null>(null)
  const [moment, setMoment] = useState('')
  const [fault, setFault] = useState('')
  const [tick, setTick] = useState(0)
  const chosen = branchId || nowBranch(branches)
  const at = momentParam(moment)

  useEffect(() => {
    if (!chosen) return
    let live = true
    api
      .signageNow(chosen, place, at)
      .then((n) => {
        if (!live) return
        setNow(n)
        setFault('')
      })
      .catch((err) => {
        if (!live) return
        setNow(null)
        setFault(at ? explain(err, lang, 'common.unreachable') : '')
      })
    return () => {
      live = false
    }
  }, [chosen, place, at, stamp, tick, lang])

  // Asked again when what plays changes by the clock, as the TV is told to.
  // A chosen moment does not move with the clock.
  const changesAt = at ? undefined : now?.changesAt
  useEffect(() => {
    const wait = refreshIn(changesAt, Date.now())
    if (wait === null) return
    const timer = window.setTimeout(() => setTick((n) => n + 1), wait)
    return () => window.clearTimeout(timer)
  }, [changesAt])

  if (branches.length === 0) return null
  const zone = branchZone(branches.find((b) => b.id === chosen)?.timezone)
  const bounds = momentBounds(new Date(), zone)
  const source =
    now && now.campaigns.length > 0
      ? now.campaigns.map((c) => c.name).join(', ')
      : now && now.items.length > 0
        ? now.design ?? say('signage.now.design')
        : say('signage.now.nothing')
  return (
    <Card className="signage-panel signage-now" role="region" aria-label={say('signage.now')}>
      <div className="signage-now__head">
        <h2 className="signage-h2">
          {!at && <i className="signage-live" aria-hidden="true" />}
          {at ? momentHeading(at, lang) : say('signage.now')}
        </h2>
        <div className="signage-now__controls">
          <PickOne hideLabel label={say('signage.branch')} value={chosen} onChange={setBranchId} options={branches.map((b) => ({ value: b.id, label: b.name }))} />
          {PLACES.length > 1 && (
            <PickOne hideLabel label={say('signage.places')} value={place} onChange={(p) => setPlace(p as SignagePlace)} options={PLACES.map((p) => ({ value: p, label: say(PLACE_KEYS[p]) }))} />
          )}
          <MomentField label={say('signage.now.when')} value={moment} min={bounds.min} max={bounds.max} onChange={setMoment} />
          {at && (
            <Button variant="ghost" size="sm" onPress={() => setMoment('')}>
              {say('signage.now.reset')}
            </Button>
          )}
        </div>
      </div>
      {fault && <Notice>{fault}</Notice>}
      {now && (
        <>
          <p className="signage-now__what">
            <strong>{source}</strong>
            {now.changesAt && <span> · {say('signage.now.until', { time: changeMoment(now.changesAt, new Date(), lang, zone) })}</span>}
          </p>
          {now.items.length > 0 && (
            <div className="signage-now__items">
              {now.items.map((item, i) => (
                <Thumb key={`${i}:${item.assetId}`} media={item} className="signage-now__thumb" />
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  )
}
